import { useCallback, useRef } from 'react';

type LongPressEvent = React.MouseEvent | React.TouchEvent;

type UseLongPressOptions = {
  /** 長押しと判定するまでの時間（ミリ秒）。デフォルト: 3000 */
  threshold?: number;
  /** 長押し完了時のコールバック */
  onLongPress: () => void;
};

type UseLongPressResult = {
  onMouseDown: (e: React.MouseEvent) => void;
  onMouseUp: (e: React.MouseEvent) => void;
  onMouseLeave: (e: React.MouseEvent) => void;
  onTouchStart: (e: React.TouchEvent) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
};

/**
 * 長押し検出用のカスタムフック
 * PC（マウス）とタッチデバイス両方に対応
 */
export const useLongPress = ({
  threshold = 3000,
  onLongPress,
}: UseLongPressOptions): UseLongPressResult => {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isLongPressTriggered = useRef(false);

  const startPressTimer = useCallback(() => {
    isLongPressTriggered.current = false;
    timerRef.current = setTimeout(() => {
      isLongPressTriggered.current = true;
      onLongPress();
    }, threshold);
  }, [onLongPress, threshold]);

  const clearPressTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const onMouseDown = useCallback(
    (_e: React.MouseEvent) => {
      startPressTimer();
    },
    [startPressTimer]
  );

  const onMouseUp = useCallback(
    (_e: React.MouseEvent) => {
      clearPressTimer();
    },
    [clearPressTimer]
  );

  const onMouseLeave = useCallback(
    (_e: React.MouseEvent) => {
      clearPressTimer();
    },
    [clearPressTimer]
  );

  const onTouchStart = useCallback(
    (_e: React.TouchEvent) => {
      startPressTimer();
    },
    [startPressTimer]
  );

  const onTouchEnd = useCallback(
    (_e: React.TouchEvent) => {
      clearPressTimer();
    },
    [clearPressTimer]
  );

  return {
    onMouseDown,
    onMouseUp,
    onMouseLeave,
    onTouchStart,
    onTouchEnd,
  };
};
